import React, {useState} from "react";
import gql from "graphql-tag";
import {useMutation} from "react-apollo";
import {string} from "yup";
import {Redirect} from "react-router-dom";
import get from "lodash/get";
import classNames from "classnames";
import {
    ErrorMessage,
    Form,
    FormGroup,
    LoadingButton,
    SubmitButton,
    TextInput,
    Stack,
} from "@unibuddy/patron";
import {Trans, useTranslation} from "@unibuddy/intl";
import compose from "lodash/flowRight";
import {useCaptcha} from "@unibuddy/captcha";

import {setMe} from "shared/Auth/actions/authActions";
import Auth from "shared/Auth/auth";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";
import {rootURL} from "shared/Utils/WhereAmI";
import AuthPanel from "shared/Register/components/AuthPanel/AuthPanel";
import {Body, Container, Header} from "shared/Register/components/VerifyLayout/Layout";
import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {PageTitle} from "../../../components/PageTitle/PageTitle";
import s from "./Verify.pcss";
import {AuthFooter} from "../../shared/components/Footer/AuthFooter";
import withAgeVerification from "./VerifyAge/AgeVerification";
import {SignupImage2} from "../Register/components/SignupImages";
import {ThemedLink} from "../../../components/ThemedLink/ThemedLink";
import {IntlNamespaces} from "../../../translations/types";
import {
    INTL_PAGE_TITLE,
    INTL_RESEND_EMAIL,
    INTL_SUCCESS_MESSAGE,
    INTL_VERIFICATION_CODE,
    INTL_VERIFICATION_INSTRUCTIONS,
    INTL_VERIFY_EMAIL,
    INTL_CHECK_EMAIL,
} from "../../../translations/verification";
import {INTL_CODE_REQUIRED} from "../../../translations/validations";

export const verifyMutation = gql`
    mutation Verify($code: String!) {
        verifyAccount(code: $code) {
            success
            error
        }
    }
`;

export const resendMutation = gql`
    mutation SendVerificationCode($source: SourceInputField!, $captureToken: String) {
        sendVerificationCode(source: $source, captureToken: $captureToken) {
            success
        }
    }
`;

// This var is used to check whether this component was rendered
// as a part of registration flow or not.
let isRegistrationFlow = false;

function Verify(props) {
    const {reportError} = useErrorReporting();
    const [verify] = useMutation(verifyMutation);
    const [resendCode, {loading}] = useMutation(resendMutation);
    const [successMessages, setSuccessMessages] = useState("");
    const [redirectData, setRedirectData] = useState("");
    const {t} = useTranslation([IntlNamespaces.VERIFICATION, IntlNamespaces.VALIDATIONS]);
    const {getCaptchaToken} = useCaptcha();

    const {
        events: {prospectAccountVerified},
    } = useAnalytics();

    const onSubmit = async ({code}, formActions) => {
        try {
            const rootUrl = `${rootURL(props.location.pathname)}`;
            const {data} = await verify({
                variables: {
                    code,
                },
            });
            if (!data.verifyAccount.success) {
                setSuccessMessages("");
                return formActions.setFieldError("serverError", data.verifyAccount.error);
            }
            isRegistrationFlow = true;
            props.dispatch(
                setMe({
                    ...props.me,
                    anyUser: {
                        ...props.me.anyUser,
                        verified: true,
                    },
                }),
            );
            prospectAccountVerified();
            if (Auth.isApplicantUniversitySetup(props.me, props.universitySlug)) {
                // Edgecase where user was added between the time this feature is launched and
                // the time university enables the feature, user will be marked as verified false
                // and goes through the verification process. However, register page2 is done
                setRedirectData({
                    pathname: get(props, "location.state.nextPathname") || `${rootUrl}/buddies`,
                    search: location.search,
                });
            } else {
                setRedirectData({
                    pathname: `${rootUrl}/auth/register2`,
                    state: {nextPathname: get(props, "location.state.nextPathname")},
                    search: get(props, "location.search", ""),
                });
            }
        } catch (error) {
            console.log(error);
            setSuccessMessages("");
            formActions.setFieldError("serverError", "Sorry, something went wrong");
            reportError(error, {tags: {location: "signup"}, level: "fatal"});
        } finally {
            formActions.setSubmitting(false);
        }
    };

    const onResend = async () => {
        const captureToken = await getCaptchaToken("SendVerificationCode");
        try {
            await resendCode({
                variables: {
                    source: {
                        type: "university",
                        slug: get(props, "universitySlug", ""),
                    },
                    captureToken,
                },
            });
            setSuccessMessages(
                t(
                    `${IntlNamespaces.VERIFICATION}:${INTL_SUCCESS_MESSAGE.key}`,
                    INTL_SUCCESS_MESSAGE.defaultValue,
                ),
            );
        } catch (error) {
            reportError(error, {tags: {location: "signup"}, level: "fatal"});
        }
    };

    if (redirectData) {
        return <Redirect to={{...redirectData}} />;
    }
    if (!isRegistrationFlow && get(props, "me.anyUser.verified", false)) {
        return <Redirect to={`${rootURL(props.location.pathname)}/buddies`} />;
    }
    const hasGloballyWrappedAccessibilityPolicyLink = props.primaryApp === "university-jigglypuff";
    return (
        <Container id="verify-page">
            <Header>
                <PageTitle
                    title={t(
                        `${IntlNamespaces.VERIFICATION}:${INTL_PAGE_TITLE.key}`,
                        INTL_PAGE_TITLE.defaultValue,
                    )}
                />
            </Header>

            <Body>
                <Stack space="large">
                    <Stack align="center" space="medium">
                        <SignupImage2 />
                        <PageHeader
                            className={s.verifyHeading}
                            title={t(
                                `${IntlNamespaces.VERIFICATION}:${INTL_CHECK_EMAIL.key}`,
                                INTL_CHECK_EMAIL.defaultValue,
                            )}
                        />
                        <p className={classNames(s.textColour, s.subHeadingText)}>
                            {t(
                                `${IntlNamespaces.VERIFICATION}:${INTL_VERIFICATION_INSTRUCTIONS.key}`,
                                INTL_VERIFICATION_INSTRUCTIONS.defaultValue,
                            )}
                        </p>
                    </Stack>
                    <AuthPanel>
                        <Stack space="large">
                            <h2 className={classNames(s.email, s.textColour)}>
                                {get(props, "me.anyUser.email", "")}
                            </h2>
                            <Form
                                onSubmit={onSubmit}
                                initialValues={{code: "", serverError: ""}}
                                validationSchema={{
                                    code: string().required(
                                        t(
                                            `${IntlNamespaces.VALIDATIONS}:${INTL_CODE_REQUIRED.key}`,
                                            INTL_CODE_REQUIRED.defaultValue,
                                        ),
                                    ),
                                }}
                            >
                                <Stack space="medium">
                                    <FormGroup
                                        id="verification-code"
                                        label={t(
                                            `${IntlNamespaces.VERIFICATION}:${INTL_VERIFICATION_CODE.key}`,
                                            INTL_VERIFICATION_CODE.defaultValue,
                                        )}
                                    >
                                        <TextInput autocomplete="one-time-code" name="code" />
                                    </FormGroup>

                                    <Stack space="medium" align="center">
                                        <SubmitButton color="primary" id="continue">
                                            {t(
                                                `${IntlNamespaces.VERIFICATION}:${INTL_VERIFY_EMAIL.key}`,
                                                INTL_VERIFY_EMAIL.defaultValue,
                                            )}
                                        </SubmitButton>
                                        <LoadingButton
                                            type="button"
                                            disabled={loading}
                                            loading={loading}
                                            color="light"
                                            onClick={onResend}
                                            id="resend-code-button"
                                        >
                                            {t(
                                                `${IntlNamespaces.VERIFICATION}:${INTL_RESEND_EMAIL.key}`,
                                                INTL_RESEND_EMAIL.defaultValue,
                                            )}
                                        </LoadingButton>
                                        {!successMessages ? (
                                            <ErrorMessage name="serverError" id="err-message" />
                                        ) : (
                                            <p
                                                data-test-id="success-message-text"
                                                className={s.textColour}
                                            >
                                                {successMessages}
                                            </p>
                                        )}
                                    </Stack>
                                </Stack>
                            </Form>
                            <Trans t={t} i18nKey="troubleSignupInstructions">
                                <p className={s.textColour}>
                                    Having trouble accessing the email address you provided? You can
                                    change the email address used to create your account by{" "}
                                    <ThemedLink
                                        id="change-verify-email-button"
                                        to={{
                                            pathname: `${rootURL(
                                                props.location.pathname,
                                            )}/auth/verify/change`,
                                            state: {
                                                nextPathname: get(
                                                    props,
                                                    "location.state.nextPathname",
                                                ),
                                            },
                                            search: get(props, "location.search"),
                                        }}
                                    >
                                        clicking here
                                    </ThemedLink>
                                    .
                                </p>
                            </Trans>
                        </Stack>
                    </AuthPanel>
                </Stack>
                <AuthFooter
                    hasGloballyWrappedAccessibilityPolicyLink={
                        hasGloballyWrappedAccessibilityPolicyLink
                    }
                />
            </Body>
        </Container>
    );
}

export default compose(withAgeVerification)(Verify);
